import React, {useState, useMemo} from "react";
import {Admission} from "@reside/reside-api-admission";
import {useNavigate} from "react-router-dom";
import {Text, theme} from "@reside/ui";
import {format} from "date-fns";

import {ModalsProviders} from "../../pages/page-admin-residents/atoms/link-another-resident-modal/ModalsProviders";
import {ResidentAdmission} from "../../pages/page-admin-residents/types";
import {ActionMenuCell} from "../admin-admissions-table/ActionMenuCell";
import {WorkflowBadge} from "../dashboard-cell/WorkflowBadge";
import {getWorkflowStyle} from "../dashboard-cell/WorkflowRibbon";
import {FacilityIconsWrapper, HouseIcon, UserIcon} from "./styles";
import {
  Column,
  ListTable,
  AdmissionStatusCell,
  PercentageCell,
} from "../list-table";
import {createPreflightUrl} from "../../pages/page-admin-preflight/route";

const ADMISSION_STATUSES = [
  Admission.StatusEnum.IN_PROGRESS,
  Admission.StatusEnum.SIGNED,
  Admission.StatusEnum.APPROVED,
  Admission.StatusEnum.CANCELLED,
  Admission.StatusEnum.ARCHIVED,
];

export const ResidentAdmissions = ({
  admissions = [],
  showMoreOptions = true,
  /** If false, will not redirect to admission page on click. */
  isRedirectToAdmissionAllowed = true,
}: {
  admissions: ResidentAdmission[];
  showMoreOptions?: boolean;
  isRedirectToAdmissionAllowed?: boolean;
}): JSX.Element => {
  const [activeRowIndex, setActiveRowIndex] = useState<number>();

  const navigate = useNavigate();

  const admissionById = useMemo(
    () => new Map(admissions.map(a => [a.id, a])),
    [admissions],
  );

  return (
    <ModalsProviders>
      <ListTable<ResidentAdmission>
        key="Resident Admissions"
        getRowId={row => row.rowData.id}
        data={admissions}
        accentBorder
        getRowProps={(row: any) => ({
          style: {
            "--row-accent-color": getWorkflowStyle(row.rowData.workflow_type)
              .color,
          } as React.CSSProperties,
        })}
        rowHeight={120}
        sortPredicates={{
          updated: ["updated"],
          date_of_admission: ["date_of_admission"],
          created: ["created"],
        }}
        onRowClick={(id: string, row) => {
          const admission = admissionById.get(id);

          if (
            !isRedirectToAdmissionAllowed ||
            !admission ||
            row.rowData.status === Admission.StatusEnum.PRE_FLIGHT
          ) {
            return;
          }

          if (ADMISSION_STATUSES.includes(admission.status)) {
            navigate(`/admin/workflows/${admission.id}`);
          }

          if (admission.status === Admission.StatusEnum.PRE_FLIGHT) {
            navigate(
              createPreflightUrl({
                facilityId: admission.facility.id,
                admissionId: admission.id,
              }),
            );
          }
        }}
        noRowsMessage="No Workflows found for this resident..."
        activeRowIndex={activeRowIndex}
      >
        <Column
          cellRenderer={(row: any) => (
            <FacilityIconsWrapper>
              <HouseIcon size={14} color={theme.color.gray100} name="house" />
              <Text color="gray100">{row.rowData?.facility?.name}</Text>
              <br />
              <UserIcon size={14} color={theme.color.gray100} name="user" />
              <Text color="gray100">
                {row.rowData?.facility?.organization?.name}
              </Text>
            </FacilityIconsWrapper>
          )}
          label="Facility"
          sortKey="residentFullName"
          width={110}
        />
        <Column
          cellRenderer={(row: any) => (
            <WorkflowBadge workflowType={row.rowData.workflow_type} />
          )}
          label="Workflow"
          sortKey="workflow_type"
          width={160}
        />
        <Column
          cellRenderer={(row: any) => <Text>{row.rowData.code}</Text>}
          label="Workflow ID"
          width={60}
        />

        <Column
          cellRenderer={(row: any) => (
            <AdmissionStatusCell status={row.rowData.status} />
          )}
          label="Status"
          sortKey="status"
          width={60}
        />

        <Column
          cellRenderer={(row: any) => (
            <PercentageCell
              data={row.rowData.admission.completion_percentage ?? 0}
            />
          )}
          label="Completed"
          sortKey="completed"
          width={60}
        />

        <Column
          cellRenderer={(row: any) => (
            <Text>
              {row.rowData.date_of_admission
                ? format(new Date(row.rowData.date_of_admission), "MM/dd/yyyy")
                : "—"}
            </Text>
          )}
          label="Date of Admission"
          sortKey={"date_of_admission"}
          width={60}
        />

        <Column
          cellRenderer={(row: any) => (
            <Text>
              {row.rowData.admission.signature_date
                ? format(
                    new Date(row.rowData.admission.signature_date),
                    "MM/dd/yyyy",
                  )
                : "—"}
            </Text>
          )}
          label="signature date"
          sortKey="signature_date"
          width={60}
        />

        <Column
          cellRenderer={(row: any) => (
            <Text>
              {row.rowData.created
                ? format(new Date(row.rowData.created), "MM/dd/yyyy")
                : "—"}
            </Text>
          )}
          label="Date added"
          sortKey="created"
          width={60}
        />

        <Column
          cellRenderer={(row: any) => (
            <Text>
              {row.rowData.updated
                ? format(new Date(row.rowData.updated), "MM/dd/yyyy")
                : "—"}
            </Text>
          )}
          label="Last Modified"
          sortKey="updated"
          width={60}
        />

        <Column
          cellRenderer={(row: any) =>
            showMoreOptions && (
              <ActionMenuCell
                data={{
                  ...row.rowData,
                  templateDocumentTypes:
                    row.rowData.admission?.template_document_types,
                }}
                onOpen={() => setActiveRowIndex(row.rowIndex)}
                onClose={() => setActiveRowIndex(undefined)}
              />
            )
          }
          label=""
          width={40}
        />
      </ListTable>
    </ModalsProviders>
  );
};
